import { v4 as uuidv4 } from 'uuid';
import { isProduction, metaConfig } from 'src/utils/config';
import { getLocalStorageValue, setLocalStorageValue } from 'src/utils/common';
import { LocalStorageKey } from 'src/utils/constants/ui-constants';
import { logger } from 'src/utils/logger';

let isPixelInitialized = false;

/**
 * Meta Pixel을 초기화합니다.
 * 프로덕션 환경에서만 동작합니다.
 * @param externalId - deviceId (Advanced Matching용)
 */
export const initMetaPixel = (externalId?: string): boolean => {
  if (!isProduction) {
    logger.logCommerce('Meta Pixel: 개발 환경에서는 비활성화됩니다.');
    return false;
  }

  if (isPixelInitialized) {
    return true;
  }

  const pixelId = metaConfig.pixelId;
  if (!pixelId) {
    logger.logCommerce('Meta Pixel ID가 설정되지 않았습니다.', { level: 'warn' });
    return false;
  }

  if (typeof window.fbq !== 'function') {
    logger.logCommerce('Meta Pixel SDK가 로드되지 않았습니다.', { level: 'warn' });
    return false;
  }

  try {
    const advancedMatching = externalId ? { external_id: externalId } : undefined;
    window.fbq('init', pixelId, advancedMatching);
    isPixelInitialized = true;
    logger.logCommerce('Meta Pixel 초기화 완료');
    return true;
  } catch (error) {
    logger.logCommerce(`Meta Pixel 초기화 실패: ${error}`, { level: 'error' });
    return false;
  }
};

/**
 * eventID를 생성합니다. (중복 제거용)
 */
export const generateEventId = (): string => {
  return uuidv4();
};

/**
 * AppFirstOpen 이벤트를 발송합니다.
 * - deviceId 기준으로 단 1회만 발송
 * - localStorage에 발송 완료 플래그 저장
 *
 * @param deviceId - device.tokenId
 * @returns 이벤트 발송 성공 여부
 */
export const sendAppFirstOpenEvent = (deviceId: string): boolean => {
  const sentDevices = getLocalStorageValue<string[]>(LocalStorageKey.appFirstOpenSent) ?? [];

  if (sentDevices.includes(deviceId)) {
    logger.logCommerce(`AppFirstOpen 이벤트 이미 발송됨 (deviceId: ${deviceId})`);
    return false;
  }

  if (!isPixelInitialized) {
    const initialized = initMetaPixel(deviceId);
    if (!initialized) {
      return false;
    }
  }

  try {
    const eventId = generateEventId();

    window.fbq('trackCustom', 'AppFirstOpen', { device_id: deviceId }, { eventID: eventId });

    const updatedSentDevices = [...sentDevices, deviceId];
    setLocalStorageValue(LocalStorageKey.appFirstOpenSent, updatedSentDevices);

    logger.logCommerce(`AppFirstOpen 이벤트 발송 완료 (deviceId: ${deviceId}, eventId: ${eventId})`);
    return true;
  } catch (error) {
    logger.logCommerce(`AppFirstOpen 이벤트 발송 실패: ${error}`, { level: 'error' });
    return false;
  }
};

/**
 * Pixel 초기화 여부 확인
 */
export const isMetaPixelReady = (): boolean => {
  return isPixelInitialized && typeof window.fbq === 'function';
};

/**
 * 테스트용 함수 - 콘솔에서 수동으로 Meta Pixel 테스트
 * 사용법: window.testMetaPixel()
 */
export const testMetaPixel = () => {
  const pixelId = metaConfig.pixelId;
  const testDeviceId = `test-device-${Date.now()}`;

  console.group('🔍 Meta Pixel Test');
  console.log('1. Environment:', isProduction ? 'Production' : 'Development');
  console.log('2. Pixel ID:', pixelId || '(not set)');
  console.log('3. fbq loaded:', typeof window.fbq === 'function');
  console.log('4. Pixel initialized:', isPixelInitialized);

  if (typeof window.fbq !== 'function') {
    console.error('❌ fbq is not loaded. Check if Meta Pixel SDK is in index.html');
    console.groupEnd();
    return;
  }

  if (!pixelId) {
    console.error('❌ Pixel ID is not set. Check REACT_APP_META_PIXEL_ID in .env');
    console.groupEnd();
    return;
  }

  try {
    // 테스트용 초기화 (isProduction 체크 무시)
    if (!isPixelInitialized) {
      window.fbq('init', pixelId, { external_id: testDeviceId });
      console.log('✅ Pixel initialized for test');
    }

    // 테스트 이벤트 발송
    const eventId = uuidv4();
    window.fbq('trackCustom', 'AppFirstOpen', { device_id: testDeviceId, test: true }, { eventID: eventId });
    console.log('✅ Test event sent!');
    console.log('   - Event ID:', eventId);
    console.log('   - Device ID:', testDeviceId);
    console.log('📌 Check Meta Events Manager > Test Events to verify');
  } catch (error) {
    console.error('❌ Error:', error);
  }

  console.groupEnd();
};

// 개발 환경에서만 window에 테스트 함수 노출
if (!isProduction && typeof window !== 'undefined') {
  (window as any).testMetaPixel = testMetaPixel;
}
